import type {
    Pointer,
    InnerPointer,
    PointerKeys,
    CompRef,
    PossibleViewModes,
    FragmentPointer
} from '@wix/document-services-types'
import _ from 'lodash'
import {displayedOnlyStructureUtil} from '@wix/santa-core-utils'
import {deepClone} from '@wix/wix-immutable-proxy'

const {getRepeaterTemplateId, getRootRefHostCompId, isRepeatedComponent} = displayedOnlyStructureUtil

const getPointer = (id: string, type: string, additionalProperties?: Partial<Pointer>): Pointer => {
    if (!id && !type) {
        console.log('illegal pointer creation !')
    }

    if (additionalProperties) {
        return {
            ...additionalProperties,
            id,
            type
        }
    }

    return {
        id,
        type
    }
}

type BaseFragmentPointerProperties = Pick<FragmentPointer, 'fragmentInstance' | 'fragmentType' | 'id' | 'type'>
type AdditionalFragmentPointerProperties = Partial<Pick<Pointer, 'innerPath' | 'variants' | 'scope'>>

const getFragmentPointer = (
    {fragmentInstance, fragmentType, id, type}: BaseFragmentPointerProperties,
    additionalProperties?: AdditionalFragmentPointerProperties
): FragmentPointer => {
    if (additionalProperties) {
        return {
            ...additionalProperties,
            fragmentInstance,
            fragmentType,
            id,
            type
        }
    }
    return {fragmentInstance, fragmentType, id, type}
}

const getCompPointer = (id: string, type: PossibleViewModes, additionalProperties?: Partial<Pointer>): CompRef =>
    getPointer(id, type, additionalProperties) as CompRef

const getPointerFromPointer = (newId: string, pointer: Pointer): Pointer => ({...pointer, id: newId})

/**
 * @param {string} id
 * @param {string} type
 * @returns {Pointer}
 */
const getPagePointer = (id: string, type: PossibleViewModes): CompRef => ({id, type, pageId: id})

const getInnerPointer = (pointer: Pointer, innerPath: string | string[]): Pointer => {
    if (_.isString(innerPath)) {
        innerPath = innerPath.split('.')
    }

    const newPointer: Pointer = {
        id: pointer.id,
        type: pointer.type,
        noRefFallbacks: pointer.noRefFallbacks,
        innerPath: pointer.innerPath ? (pointer.innerPath as string[]).concat(innerPath) : innerPath
    }

    if (pointer.scope) {
        newPointer.scope = pointer.scope
    }

    if (pointer.fragmentType) {
        newPointer.fragmentType = pointer.fragmentType
    }

    if (pointer.fragmentInstance) {
        newPointer.fragmentInstance = pointer.fragmentInstance
    }

    return newPointer
}

const getInnerValue = (pointer: Pointer, rootValue: any): any => {
    if (_.isNil(rootValue) || !pointer.innerPath) {
        return rootValue
    }
    return _.get(rootValue, pointer.innerPath)
}

const getOriginalPointerFromInner = (pointer: Pointer): Pointer => getPointer(pointer.id, pointer.type)

const pointerKeys: PointerKeys = [
    'id',
    'type',
    'innerPath',
    'pageId',
    'useLanguage',
    'multilingual',
    'noRefFallbacks',
    'doNotSync',
    'variants',
    'scope',
    'fragmentType',
    'fragmentInstance'
]
const pointerKeysSet: Set<string> = new Set(pointerKeys)

const isPointer = (obj: any, isStrict: boolean = true): obj is Pointer => {
    if (!obj?.id || !obj.type) {
        return false
    }

    if (!isStrict) {
        return true
    }

    return Object.keys(obj).every(key => pointerKeysSet.has(key))
}

const isFragmentPointer = (pointer: Pointer): pointer is FragmentPointer => !!pointer.fragmentType

type OptionalPointer = Pointer | null | undefined

const isSameComponent = (p1: OptionalPointer, p2: OptionalPointer, scopesHybridModeDisabled: boolean): boolean => {
    if (!p1 || !p2 || p1.type !== p2.type || p1.id !== p2.id || !_.isEqual(p1.innerPath, p2.innerPath)) {
        return false
    }

    if (!scopesHybridModeDisabled) {
        return true
    }

    if (!p1.scope && !p2.scope) {
        return true
    }

    return isSameComponent(p1.scope, p2.scope, scopesHybridModeDisabled)
}

const sortVariantsItems = (p: OptionalPointer): void => {
    if (Array.isArray(p?.variants)) {
        p!.variants.sort((a, b) => {
            if (a.id < b.id) return -1
            if (a.id > b.id) return 1
            return 0
        })
    }
}

const arePropertiesEqual = (p1: OptionalPointer, p2: OptionalPointer): boolean => {
    return pointerKeys.every(key => key === 'scope' || _.isEqual(p1?.[key], p2?.[key]))
}

const comparePointerAfterVariantsSorting = (
    p1: OptionalPointer,
    p2: OptionalPointer,
    scopesHybridModeDisabled: boolean
): boolean => {
    const clonePointer1 = deepClone(p1)
    const clonePointer2 = deepClone(p2)

    sortVariantsItems(clonePointer1)
    sortVariantsItems(clonePointer2)

    if (scopesHybridModeDisabled) {
        return _.isEqual(clonePointer1, clonePointer2)
    }

    return arePropertiesEqual(clonePointer1, clonePointer2)
}

const isSamePointer = (p1: OptionalPointer, p2: OptionalPointer, scopesHybridModeDisabled: boolean): boolean => {
    if (!p1 || !p2) {
        return false
    }

    if (scopesHybridModeDisabled) {
        return _.isEqual(p1, p2) || comparePointerAfterVariantsSorting(p1, p2, scopesHybridModeDisabled)
    }

    const hasMatchingKeys = arePropertiesEqual(p1, p2)

    if (!hasMatchingKeys && !comparePointerAfterVariantsSorting(p1, p2, scopesHybridModeDisabled)) {
        return false
    }

    return isSameComponent(p1, p2, scopesHybridModeDisabled)
}

const getInnerPointerPathRoot = ({innerPath}: Pointer) => (innerPath ? innerPath[0] : null)

/**
 * @param {Pointer} pointer
 * @return {Pointer}
 */
const stripInnerPath = (pointer: Pointer): Pointer => {
    if (pointer.innerPath) {
        const ptr: Pointer = {id: pointer.id, type: pointer.type}
        for (const key in pointer) {
            if (key !== 'innerPath' && !ptr.hasOwnProperty(key)) {
                ptr[key] = pointer[key]
            }
        }
        return ptr
    }
    return pointer
}

const normalizeInnerPath = (innerPath?: string[] | string): string[] => {
    if (innerPath === undefined) {
        return []
    }
    if (Array.isArray(innerPath)) {
        return innerPath
    }
    return ([] as string[]).concat(innerPath)
}

/**
 * A utility for getting the inner path. This is to avoid multiple places that deal with normalization
 * @param pointer
 * @returns {string[]}
 */
const getInnerPath = (pointer: Pointer): string[] => normalizeInnerPath(pointer.innerPath)

/**
 * Checks if the pointer has an innerPath
 * @param pointer
 * @returns {boolean}
 */
const hasInnerPath = (pointer: Pointer): pointer is InnerPointer => getInnerPath(pointer).length > 0

/**
 * @param {Pointer} basePointer
 * @returns {CompRef}
 */
const getRepeatedItemPointerIfNeeded = (basePointer: Pointer): CompRef =>
    (isRepeatedComponent(basePointer.id)
        ? getPointerFromPointer(getRepeaterTemplateId(basePointer.id), basePointer)
        : basePointer) as CompRef

const getRootRefHostPointerFromPointer = (basePointer: Pointer): CompRef =>
    getPointerFromPointer(getRootRefHostCompId(basePointer.id), basePointer) as CompRef

/**
 * @param {Pointer} basePointer
 * @returns {Pointer}
 */
const signaturePath = ['metaData', 'sig']
const getSignaturePointer = (basePointer: Pointer): Pointer => getInnerPointer(basePointer, signaturePath)
const getOverrideComponentPointer = (id: string, viewMode: string, queryName: string): CompRef | null => {
    if (!id.endsWith(queryName)) {
        return null
    }
    return getPointer(id.replace(new RegExp(`-${queryName}$`), ''), viewMode) as CompRef
}

export {
    getPointer,
    getCompPointer,
    getPagePointer,
    getInnerPointer,
    getInnerValue,
    getOriginalPointerFromInner,
    isPointer,
    isFragmentPointer,
    getFragmentPointer,
    isSameComponent,
    isSamePointer,
    getInnerPointerPathRoot,
    stripInnerPath,
    getInnerPath,
    hasInnerPath,
    normalizeInnerPath,
    getPointerFromPointer,
    getRepeatedItemPointerIfNeeded,
    getRootRefHostPointerFromPointer,
    getSignaturePointer,
    getOverrideComponentPointer
}
